import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { getDonationImage } from '../../../../constants/images';
import { pngIcons } from '../../../../constants/pngIcon';
import { SequenceImageType } from '../../../../constants/sequenceImages';
import { SecondParameter } from '../../../../models/Utils';
import { PngIcon, SequenceImage } from '../../../Molecules';

import styles from './DonateBreadTemplate.module.css';

type DonateBreadTemplateProps = {
  donationImageSet: number;
  onClose: () => void;
};

enum Step {
  Hungry = 0,
  BreadFlying = 1,
  Staff = 2,
  Giving = 3,
  ThankYou = 4,
}

interface AnimationImage {
  id: number;
  startX: number;
}

const FlyingBreads = () => {
  const [images, setImages] = useState<AnimationImage[]>([]);
  const imageSrc = pngIcons.Bread;
  const [showLightGlow, setShowLightGlow] = useState<boolean>(false);

  const addImage = useCallback((isLeft: boolean) => {
    const halfRandom = Math.random() * (window.innerWidth / 2); // Random x position at the half of top
    const newImage = {
      id: Date.now(),
      startX: isLeft ? halfRandom : window.innerWidth - halfRandom,
    };
    setImages((prev) => [...prev, newImage]);

    // // Automatically remove the image after the animation ends (e.g., 3 seconds)
    // setTimeout(() => {
    //   setImages((prev) => prev.filter((img) => img.id !== newImage.id));
    // }, 1000);
  }, []);

  useEffect(() => {
    const startTime = new Date().getTime();
    let isLeft = true;
    addImage(isLeft);

    const interval = setInterval(() => {
      if (new Date().getTime() - startTime > 500) {
        clearInterval(interval);
        return;
      }
      isLeft = !isLeft;
      addImage(isLeft);
    }, 60);
    return () => clearInterval(interval);
  }, [addImage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowLightGlow(true);
    }, 550);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <AnimatePresence>
        {images.map((image) => (
          <motion.img
            key={image.id}
            src={imageSrc}
            alt="Flying"
            className={styles.breadAnimation}
            initial={{
              top: 0,
              left: image.startX,
              opacity: 1,
            }}
            animate={{
              top: '50%',
              left: '50%',
              opacity: 0,
              scale: 1.5,
              x: '-50%',
              y: '-50%',
            }}
            exit={{
              opacity: 0,
              scale: 0,
              transition: { duration: 0.2 },
            }}
            transition={{
              duration: 0.8,
              ease: 'easeInOut',
            }}
          />
        ))}
      </AnimatePresence>
      {showLightGlow && (
        <div className={styles.lightGlow}>
          <SequenceImage type={SequenceImageType.WhiteLightGlow} />
        </div>
      )}
    </>
  );
};

export const DonateBreadTemplate = ({
  donationImageSet,
  onClose,
}: DonateBreadTemplateProps) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(Step.Hungry);

  let imageType: SecondParameter<typeof getDonationImage>;
  switch (step) {
    case Step.Staff:
      imageType = 'staff';
      break;
    case Step.Giving:
      imageType = 'giving';
      break;
    case Step.ThankYou:
      imageType = 'bread';
      break;
    default:
      imageType = 'hungry';
      break;
  }

  useEffect(() => {
    if (step < Step.ThankYou) {
      const timeout = setTimeout(() => {
        setStep(step + 1);
      }, 1500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [step]);

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <img
            src={getDonationImage(donationImageSet, imageType)}
            className={styles.image}
            alt=""
          />
        </div>
        {step === Step.ThankYou && (
          <>
            <div className={styles.top}>
              <PngIcon name="Heart" height={60} width={60} />
              <div>{t('Thank You!')}</div>
              <div>{t('You have saved more children from hunger!')}</div>
            </div>
            <div className={styles.bottom}>
              <div>
                {t(
                  'JOYAJOY will donate the same amount of bread to hungry children in the real world.',
                )}
              </div>
              <div className={styles.button} onClick={onClose}>
                {t('OK')}
              </div>
            </div>
          </>
        )}
      </div>
      {step === Step.BreadFlying && <FlyingBreads />}
    </div>
  );
};
